import './signInPinVerifyPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { CheckCircleIcon } from '../../components/Icons/CheckCircleIcon';
import { ErrorOutlineIcon } from '../../components/Icons/ErrorOutlineIcon';
import { OTP } from '../../components/OTP/OTP';
import { Toast } from '../../components/Toast/Toast';
import useApiStore from '../../store/apiStore/useApiStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignOutStore from '../../store/signOutStore/useSignOutStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const {
    clearPin,
    deliverPin,
    deliverPinFailedMessage,
    deliverPinRetrySuccess,
    pin,
    primaryPhoneNumber,
    setPin,
    computed: { hasValidPin },
  } = usePinStore();
  const {
    clearPinRetryState,
    userAuthenticate,
    userAuthenticateFailedMessage,
    userAuthenticateLocked,
    userAuthenticateSuccess,
  } = useUserStore();
  const { signOut } = useSignOutStore();
  const { leadChannelCode } = useParameterStore();

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('SignInPinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('SignInPinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (primaryPhoneNumber && pin) {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        AuthType.ACCOUNT_SIGN_IN_USING_PIN,
      );
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();
      clearPinRetryState();
      await deliverPin({
        deliverPinLocation: DeliverPinLocation.RETRY,
        phone: primaryPhoneNumber,
        purpose: AuthType.ACCOUNT_SIGN_IN_USING_PIN,
      });
      clearPin();
    }
  };

  useEffect((): (() => void) => {
    if (!primaryPhoneNumber) {
      navigate('/sign-in');
    }
    if (userAuthenticateLocked) {
      signOut({
        withMessage: userAuthenticateFailedMessage,
      });
    }
    startTimer();
    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber, userAuthenticateLocked, userAuthenticateSuccess]);

  useEffect((): void => {
    if (userAuthenticateFailedMessage) {
      clearPin();
    }
  }, [userAuthenticateFailedMessage]);

  useEffect((): (() => void) => {
    // Clearing pin on unmount so the next pin page starts fresh
    return (): void => {
      clearPin();
    };
  }, []);

  useEffect((): void => {
    // Auto-submit once the last digit is entered
    if (hasValidPin) {
      formSubmit();
    }
  }, [hasValidPin]);

  return (
    <div className="sign-in-pin-verify-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Toast
            open={deliverPinRetrySuccess}
            onClose={(): void => {
              usePinStore.setState({ deliverPinRetrySuccess: false });
            }}
          >
            <Alert
              data-testid={locator.pages.signInPinVerify.alert.success}
              icon={<CheckCircleIcon />}
              severity="success"
            >
              {t('SignInPinVerifyPage.codeSent')}
            </Alert>
          </Toast>

          <Toast
            open={!isEmpty(deliverPinFailedMessage)}
            onClose={(): void => {
              usePinStore.setState({ deliverPinFailedMessage: null });
            }}
          >
            <Alert
              data-testid={locator.pages.signInPinVerify.alert.error}
              icon={<ErrorOutlineIcon />}
              severity="error"
            >
              {deliverPinFailedMessage}
            </Alert>
          </Toast>

          <Header
            trailing={
              <Link
                data-testid={locator.pages.signInPinVerify.header.signUpLink}
                to="/sign-up"
              >
                {t('SignInPinVerifyPage.signup')}
              </Link>
            }
            showLogo
          />

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signInPinVerify.form.root}
              noValidate
              onSubmit={formSubmit}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signInPinVerify.form.h1}>
                  {t('SignInPinVerifyPage.title')}
                </h1>
                <h3 data-testid={locator.pages.signInPinVerify.form.h3}>
                  <Trans
                    components={[
                      <span
                        key={uniqueId('span-')}
                        style={{ textDecoration: 'underline' }}
                      />,
                    ]}
                    i18nKey={'SignInPinVerifyPage.subtitle'}
                    values={{
                      phoneNumber: toPhoneString(primaryPhoneNumber),
                    }}
                  />
                </h3>

                <OTP
                  disabled={isLoading}
                  error={userAuthenticateFailedMessage}
                  errorTestId={locator.pages.signInPinVerify.form.pinInputError}
                  inputTestId={locator.pages.signInPinVerify.form.pinInput}
                  value={pin}
                  onChange={setPin}
                />

                <Button
                  data-testid={locator.pages.signInPinVerify.form.resendPinLink}
                  disabled={seconds > 0}
                  fullWidth
                  onClick={resendCode}
                >
                  {buttonText}
                </Button>
              </div>
              <footer data-testid={locator.pages.signInPinVerify.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignInPinVerifyPage';
